// ========================================
// HOOK useProjects - VERSION OPTIMISÉE
// Avec cache global pour éviter les rechargements
// ========================================

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getAllProjects,
  getProjectBySlug,
  getFeaturedProjects,
  addProject,
  updateProject,
  deleteProject,
  reorderProjects
} from '../services/firebase/projectsService';
import { toast } from 'react-toastify';

// ============================================
// CACHE GLOBAL (persiste entre les composants)
// ============================================
let projectsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Promise partagée pour éviter les appels simultanés
let fetchPromise = null;

const isCacheValid = () => {
  if (!projectsCache || !cacheTimestamp) return false;
  return Date.now() - cacheTimestamp < CACHE_DURATION;
};

// ============================================
// HOOK PRINCIPAL
// ============================================
const useProjects = (autoFetch = true) => {
  const [projects, setProjects] = useState(projectsCache || []);
  const [loading, setLoading] = useState(!isCacheValid());
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  // Charger tous les projets
  const fetchProjects = useCallback(async (forceRefresh = false) => {
    // Si cache valide et pas de forceRefresh, utiliser le cache
    if (!forceRefresh && isCacheValid()) {
      if (mountedRef.current) {
        setProjects(projectsCache);
        setLoading(false);
      }
      return { success: true, data: projectsCache };
    }

    // Si un fetch est déjà en cours, attendre son résultat
    if (fetchPromise) {
      const result = await fetchPromise;
      if (mountedRef.current) {
        setProjects(result.data || []);
        setLoading(false);
      }
      return result;
    }

    // Lancer le fetch
    if (mountedRef.current) {
      setLoading(true);
      setError(null);
    }

    // Créer la promise partagée
    fetchPromise = getAllProjects();

    try {
      const result = await fetchPromise;

      if (result.success) {
        // Mettre en cache
        projectsCache = result.data;
        cacheTimestamp = Date.now();

        if (mountedRef.current) {
          setProjects(result.data);
        }
      } else {
        if (mountedRef.current) {
          setError(result.error);
        }
      }

      return result;
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message);
      }
      return { success: false, error: err.message };
    } finally {
      fetchPromise = null;
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Charger au montage si autoFetch
  useEffect(() => {
    mountedRef.current = true;

    if (autoFetch) {
      fetchProjects();
    }

    return () => {
      mountedRef.current = false;
    };
  }, [autoFetch, fetchProjects]);

  // Récupérer un projet par slug (depuis le cache si possible)
  const getProjectBySlugCached = useCallback(async (slug) => {
    // D'abord chercher dans le cache
    if (projectsCache?.length) {
      const found = projectsCache.find(p => p.slug === slug);
      if (found) {
        return { success: true, data: found };
      }
    }

    // Sinon, charger depuis Firebase
    return await getProjectBySlug(slug);
  }, []);

  // Ajouter un projet
  const createProject = async (projectData) => {
    const result = await addProject(projectData);

    if (result.success) {
      toast.success('Projet créé avec succès !');
      // Invalider le cache et recharger
      projectsCache = null;
      cacheTimestamp = null;
      await fetchProjects(true);
      return { success: true, id: result.id };
    } else {
      toast.error('Erreur lors de la création du projet');
      return { success: false, error: result.error };
    }
  };

  // Modifier un projet
  const editProject = async (projectId, projectData) => {
    const result = await updateProject(projectId, projectData);

    if (result.success) {
      toast.success('Projet mis à jour !');
      // Invalider le cache et recharger
      projectsCache = null;
      cacheTimestamp = null;
      await fetchProjects(true);
      return { success: true };
    } else {
      toast.error('Erreur lors de la mise à jour');
      return { success: false, error: result.error };
    }
  };

  // Supprimer un projet
  const removeProject = async (projectId) => {
    const result = await deleteProject(projectId);

    if (result.success) {
      toast.success('Projet supprimé');
      // Mettre à jour le cache localement
      if (projectsCache) {
        projectsCache = projectsCache.filter(p => p.id !== projectId);
      }
      setProjects(prev => prev.filter(p => p.id !== projectId));
      return { success: true };
    } else {
      toast.error('Erreur lors de la suppression');
      return { success: false, error: result.error };
    }
  };

  // Réordonner les projets
  const reorder = async (newOrder) => {
    const result = await reorderProjects(newOrder);

    if (result.success) {
      projectsCache = newOrder;
      setProjects(newOrder);
      return { success: true };
    } else {
      toast.error('Erreur lors de la réorganisation');
      return { success: false };
    }
  };

  // Invalider le cache manuellement
  const invalidateCache = useCallback(() => {
    projectsCache = null;
    cacheTimestamp = null;
  }, []);

  return {
    projects,
    loading,
    error,
    fetchProjects,
    createProject,
    editProject,
    removeProject,
    reorder,
    getProjectBySlug: getProjectBySlugCached,
    getFeaturedProjects,
    invalidateCache
  };
};

export default useProjects;